import React, { useState, useEffect, useMemo } from "react";
import { Formik } from "formik";
import Swal from "sweetalert2";
import * as Yup from "yup";
import CustomCkeditor from "../../common/customeditor";
import { useNavigate } from "react-router-dom";
import { REMEDY_FIELDS, SWAL_SETTINGS } from "../../../../utils/Constants";
import { handleServerValidations } from "../../../../utils/commonfunction";
import Breadcrums from "../../common/breadcrumbs";
import { getAllServiceList } from "../../services/common.services";

const getValidationSchema = (type) => {
    const attrFields = REMEDY_FIELDS[type] || [];

    // const attrsSchema = attrFields.reduce((acc, field) => {
    //     console.log("Field in validation schema:=-=---->", field);
    //     acc[field.name] = Yup.string().required(`${field.label} is required`);
    //     return acc;
    // }, {});
    const attrsSchema = attrFields.reduce((acc, field) => {
        if (typeof field.type === "string") {
            // Apply validation for string type fields
            acc[field.name] = Yup.string().trim().max(100, `${field.label} must be at most 100 characters`).required(`${field.label} is required`);
        } else if (typeof field.type === "number") {
            // Apply validation for number type fields
            acc[field.name] = Yup.number().typeError(`${field.label} must be a number`).max(1000000, `${field.label} must be at most 1000000`).required(`${field.label} is required`);
        }
        return acc;
    }, {});
    return Yup.object({
        name: Yup.string().trim().max(100, "Name must be at most 100 characters").required("Name is required"),
        remedy_type: Yup.string().required("Remedy type is required"),
        price: Yup.number().typeError("Must be a number").min(0, "Price must be at least 0").max(1000000, "Price must be at most 1000000").required("Price is required"),
        delivery_charge: Yup.number().typeError("Must be a number").min(0, "Delivery charge must be at least 0").max(100000, "Delivery charge must be at most 100000").nullable(),
        discount: Yup.number().typeError("Must be a number").min(0, "Discount must be at least 0").max(100, "Discount must be at most 100").nullable(),
        gst: Yup.number().typeError("Must be a number").min(0, "GST must be at least 0").max(100, "GST must be at most 100").nullable(),
        stock: (type === 'online_puja' || type === 'puja' || type === 'online-puja' || type === 'Online Puja') ? Yup.mixed().notRequired() : Yup.number().typeError("Must be a number").min(0, "Stock must be at least 0").max(10000, "Stock must be at most 10000").integer("Stock must be an integer").required("Stock is required"),
        rating: Yup.number().typeError("Must be a number").min(0, "Rating must be at least 0").max(5, "Rating must be at most 5"),
        description: Yup.string().required("Description is required"),
        image: Yup.mixed().required("Image is required"),
        attributes: Yup.object(attrsSchema),
    });
};

const RemedyAddEdit = ({ initialValues, onSubmit, isEdit = false }) => {
    const [remedyTypeList, setRemedyTypeList] = useState([]);
    const [previewimage, setPreviewImage] = useState("");
    const remedyTypeMap = useMemo(() => {
        const map = {};
        remedyTypeList.forEach((type) => {
            map[type._id] = type.slug; 
        });
        // Optional: Add fallback for hardcoded ones if needed
        map["gemstone"] = "Gemstone";
        map["rudraksha"] = "Rudraksha";
        map["puja"] = "Puja"; // or "Online Puja" depending on your data
        map["yantra"] = "Yantra";
        return map;
    }, [remedyTypeList]);
    const [type, setType] = useState(remedyTypeMap[initialValues.remedy_type] ? remedyTypeMap[initialValues.remedy_type] : "");
    const remedyTypeTitleMap = useMemo(() => {
        const map = {};
        remedyTypeList.forEach((type) => {
            map[type._id] = type.title; 
        });
        map["puja"] = "Online Puja";
        return map;
    }, [remedyTypeList]);
    const navigate = useNavigate();
    const breadcrumbs = [
        { title: "Dashboard", url: "/admin/dashboard" },
        { title: "Remedies", url: "/admin/remedies-management/remedies/list/1" },
        { title: isEdit ? "Edit Remedy" : "Add Remedy", url: "" },
    ];

    useEffect(() => {
        console.log("Initial Values:", initialValues);
        console.log("Remedy Type Map on Init:", remedyTypeMap);
        setType(remedyTypeMap[initialValues.remedy_type] ? remedyTypeMap[initialValues.remedy_type] : "");
        // If editing with image, set preview
        if (initialValues.image && typeof initialValues.image === "string") {
            setPreviewImage(initialValues.image);
        }
    }, [initialValues, remedyTypeMap]);

     useEffect(() => {
        getAllServiceList("remedies").then((response) => {
            console.log("response-remedy type-->", response.data);
            setRemedyTypeList(response.data || []);
        }).catch((error) => {
            console.log(error);
        });
    }, []);

    console.log("Remedy Type Map: ", remedyTypeMap);
    console.log("type--->: ", type);

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <Formik
                initialValues={initialValues}
                validationSchema={getValidationSchema(type)}
                enableReinitialize
                onSubmit={(values, { setSubmitting, resetForm }) => {
                    let formData = new FormData();
                    if (isEdit) {
                        formData.append("o_id", initialValues.o_id);
                    }
                    formData.append("name", values.name);
                    formData.append("remedy_type", values.remedy_type);
                    formData.append("price", values.price);
                    formData.append("delivery_charge", values.delivery_charge || 0);
                    formData.append("discount", values.discount || 0);
                    formData.append("gst", values.gst || 0);
                    formData.append("rating", values.rating);
                    formData.append("stock", values.stock || 0);
                    formData.append("refundable", values.refundable ? "true" : "false");
                    formData.append("description", values.description);
                    formData.append("image", values.image);
                    formData.append("attributes", JSON.stringify(values.attributes));

                    onSubmit(formData)
                        .then((response) => {
                            setSubmitting(false);
                            if (response.success) {
                                Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
                                resetForm({ values: "" });
                                setPreviewImage("");
                                setType("");
                                setSubmitting(false);
                                navigate(`/admin/remedies-management/remedies/list/1`);
                            } else {
                                Swal.fire({ icon: "error", text: handleServerValidations(response) , ...SWAL_SETTINGS });
                            }
                        })
                        .catch(() => {
                            setSubmitting(false);
                            Swal.fire({ icon: "error", text: "Network or server error", ...SWAL_SETTINGS });
                        });
                }}
            >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched, isSubmitting }) => (
                    <form onSubmit={handleSubmit}>
                    {console.log("Values:----->", values)}
                    {console.log("Errors:----->", errors)}
                        <div className="row row-sm">
                            <div className="col-lg-12 col-md-12 animation_fade">
                                <div className="card custom-card">
                                    <div className="card-body">
                                        <div>
                                            <h6 className="main-content-label mb-3">{isEdit ? "Edit" : "Add"} Remedy</h6>
                                        </div>
                                        <div className="row row-sm">
                                            {/* Remedy Type Select */}
                                            <div className="form-group col-lg-6">
                                                <label>
                                                    Remedy Type <span className="requirestar">*</span>
                                                </label>
                                                <select
                                                    name="remedy_type"
                                                    value={values.remedy_type}
                                                    onChange={(e) => {
                                                        setType(remedyTypeMap[e.target.value] ? remedyTypeMap[e.target.value] : "");
                                                        setFieldValue("remedy_type", e.target.value);
                                                        setFieldValue("attributes", {});
                                                    }}
                                                    onBlur={handleBlur}
                                                    className="form-control"
                                                >
                                                    <option value="">Select Type</option>
                                                    {/* <option value="gemstone">Gemstone</option>
                                                    <option value="rudraksha">Rudraksha</option>
                                                    <option value="puja">Online Puja</option>
                                                    <option value="yantra">Yantra</option> */}
                                                    {remedyTypeList &&
                                                        remedyTypeList.length > 0 &&
                                                        remedyTypeList.map((item) => (
                                                            <option key={item._id} value={item._id}>
                                                                {item.title}
                                                            </option>
                                                        ))}<br />
                                                </select>
                                                <span className="text-danger">{touched.remedy_type && errors.remedy_type}</span>
                                            </div>

                                            {/* Name */}
                                            <div className="form-group col-lg-6">
                                                <label>
                                                    Name <span className="requirestar">*</span>
                                                </label>
                                                <input name="name" type="text" value={values.name} onChange={handleChange} onBlur={handleBlur} className="form-control" placeholder="Remedy Name" />
                                                <span className="text-danger">{touched.name && errors.name}</span>
                                            </div>

                                            {/* Price */}
                                            <div className="form-group col-lg-6">
                                                <label>
                                                    Price <span className="requirestar">*</span>
                                                </label>
                                                <input name="price" type="number" value={values.price} onChange={handleChange} onBlur={handleBlur} className="form-control" placeholder="₹ Price" />
                                                <span className="text-danger">{touched.price && errors.price}</span>
                                            </div>
                                            {/* Discount */}
                                            <div className="form-group col-lg-6">
                                                <label>
                                                    Discount (%) 
                                                </label>
                                                <input name="discount" type="number" value={values.discount} onChange={handleChange} onBlur={handleBlur} className="form-control" placeholder="Discount Percentage" />
                                                <span className="text-danger">{touched.discount && errors.discount}</span>
                                            </div>
                                            {/* Delivery Charge */}
                                            <div className="form-group col-lg-6">
                                                <label>
                                                    Delivery Charge (₹)
                                                </label>
                                                <input name="delivery_charge" type="number" value={values.delivery_charge} onChange={handleChange} onBlur={handleBlur} className="form-control" placeholder="₹ Delivery Amount" />
                                                <span className="text-danger">{touched.delivery_charge && errors.delivery_charge}</span>
                                            </div>
                                            {/* GST */}
                                            <div className="form-group col-lg-6">
                                                <label>
                                                    GST / Tax (%)
                                                </label>
                                                <input name="gst" type="number" value={values.gst} onChange={handleChange} onBlur={handleBlur} className="form-control" placeholder="GST Percentage (e.g. 18)" />
                                                <span className="text-danger">{touched.gst && errors.gst}</span>
                                            </div>
                                            {/* Rating */}
                                            {/* <div className="form-group col-lg-6">
                                                <label>
                                                    Rating <span className="requirestar">*</span>
                                                </label>
                                                <input name="rating" type="number" step="0.1" value={values.rating} onChange={handleChange} onBlur={handleBlur} className="form-control" placeholder="Rating out of 5" />
                                                <span className="text-danger">{touched.rating && errors.rating}</span>
                                            </div> */}
                                            {/* Rating */}
                                            <div className="form-group col-lg-6">
                                                <label>
                                                    Admin Rating 
                                                    {values.average_rating ? (
                                                        <span className="ml-2 text-muted" style={{ fontSize: "14px" }}>
                                                            (User Avg Rating: {values.average_rating})
                                                        </span>
                                                    ) : null}
                                                    {/* <span className="requirestar">*</span> */}
                                                </label>

                                                <input 
                                                    name="rating"
                                                    type="number"
                                                    step="0.1"
                                                    value={values.rating}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    className="form-control"
                                                    placeholder="Admin rating out of 5" 
                                                />

                                                <span className="text-danger">{touched.rating && errors.rating}</span>
                                            </div>

                                            {/* Show Average Rating ONLY in Edit mode */}
                                            {values._id && values.average_rating !== undefined && (
                                                <div className="form-group col-lg-6">
                                                    <label>User Average Rating</label>
                                                    <input 
                                                        type="number"
                                                        className="form-control"
                                                        value={values.average_rating}
                                                        disabled
                                                    />
                                                </div>
                                            )}
                                           {/* Refundable Checkbox - Aligned with other fields */}
                                            <div className="col-lg-6 mb-3 d-flex align-items-end">
                                                <div className="w-100">
                                                    <label className="form-label mb-2">Refundable</label>
                                                    <div className="form-check form-switch" style={{ marginLeft: "20px" }}> {/* Using form-switch for modern look */}
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            role="switch"
                                                            id="refundableSwitch"
                                                            name="refundable"
                                                            checked={values.refundable || false}
                                                            onChange={(e) => setFieldValue("refundable", e.target.checked)}
                                                            style={{ width: "50px", height: "26px" }} // Larger toggle
                                                        />
                                                        <label className="form-check-label ms-3" htmlFor="refundableSwitch">
                                                            {values.refundable ? "Yes, this remedy is refundable" : "No, not refundable"}
                                                        </label>
                                                    </div>
                                                    <small className="text-muted">
                                                        Allow customers to return this remedy if eligible
                                                    </small>
                                                </div>
                                            </div>
                                            
                                            {/* Stock */}
                                            {remedyTypeTitleMap[values.remedy_type] === 'Online Puja' || remedyTypeTitleMap[values.remedy_type] === 'Online Pooja' || type === 'online_puja' || type === 'puja' || type === 'online-puja' ? null : (
                                            <div className="form-group col-lg-6">
                                                <label>
                                                    Stock <span className="requirestar">*</span>
                                                </label>
                                                <input name="stock" type="number" value={values.stock} onChange={handleChange} onBlur={handleBlur} className="form-control" placeholder="Stock Quantity" />
                                                <span className="text-danger">{touched.stock && errors.stock}</span>
                                            </div>
                                            )}

                                            {/* Dynamic fields by remedy type */}
                                            {(REMEDY_FIELDS[type] || []).map((field) => (
                                                <div key={field.name} className="form-group col-lg-6">
                                                    <label>
                                                        {field.label}
                                                        <span className="requirestar">*</span>
                                                    </label>
                                                    <input
                                                        name={`attributes.${field.name}`}
                                                        type={field.type}
                                                        value={values.attributes[field.name] || ""}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        className="form-control"
                                                        placeholder={field.label}
                                                    />
                                                    <span className="text-danger">{touched.attributes?.[field.name] && errors.attributes?.[field.name]}</span>
                                                </div>
                                            ))}

                                            {/* CKEditor Description */}
                                            <div className="form-group">
                                                <label htmlFor="description" className="text-left d-flex cp">
                                                    Description :<span className="requirestar">*</span>
                                                </label>
                                                <CustomCkeditor fieldname={"description"} setFieldValue={setFieldValue} value={values?.description} setFieldTouched={setFieldTouched} />
                                                <span className="text-danger d-flex text-left">{errors.description && touched.description && errors.description}</span>
                                            </div>

                                            {/* File/Image Input */}
                                            <div className="form-group col-lg-6">
                                                <label htmlFor="image" className="text-left d-flex">
                                                    Image :<span className="requirestar">*</span>
                                                </label>
                                                <input
                                                    className="form-control imgInput"
                                                    id="image"
                                                    name="image"
                                                    accept="image/*"
                                                    type="file"
                                                    onChange={(event) => {
                                                        setFieldValue("image", event.currentTarget.files[0] || "");
                                                        event.currentTarget.files.length === 1 ? setPreviewImage(URL.createObjectURL(event.currentTarget.files[0])) : setPreviewImage("");
                                                    }}
                                                />
                                                <span className="text-danger d-flex text-left" id="errortext">
                                                    {errors.image && touched.image && errors.image}
                                                </span>
                                                {previewimage ? (
                                                    <ul className="question-image-preview questions-ul">
                                                        <li className="pr_img_box">
                                                            <img src={previewimage} style={{ height: "100px" }} alt="Remedy" />
                                                            <div className="img_options">
                                                                <button
                                                                    type="button"
                                                                    className="text-danger"
                                                                    onClick={() => {
                                                                        setPreviewImage("");
                                                                        setFieldValue("image", "");
                                                                        document.getElementById("image").value = "";
                                                                    }}
                                                                >
                                                                    <i className="ri-delete-bin-6-fill"></i>
                                                                </button>
                                                            </div>
                                                        </li>
                                                    </ul>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="">
                                            <button type="submit" className="btn btn-main-primary signbtn mr-2" disabled={isSubmitting}>
                                                <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                                {isEdit ? "Update" : "Submit"}
                                            </button>
                                            <button type="button" className="btn btn-secondary" onClick={() => window.history.back()}>
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                )}
            </Formik>
        </>
    );
};

export default RemedyAddEdit;

